import {
  getLeoUuid,
  LeoUUID,
  getObject,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { SurveyDetailsAndDashboardViews, SurveyDetailsAndDashboardViewsDTO } from "./surveyDetailsAndDashboardViews";
/* eslint-disable import/export */
export abstract class GetSurveyDashboardViewsRPC implements
  LeoRPC<GetSurveyDashboardViewsRPC.Request, GetSurveyDashboardViewsRPC.Response, GetSurveyDashboardViewsRPC.Errors.Errors>
{
  abstract execute(
    request: GetSurveyDashboardViewsRPC.Request
  ): Promise<LeoRPCResult<GetSurveyDashboardViewsRPC.Response, GetSurveyDashboardViewsRPC.Errors.Errors>>;
}

export namespace GetSurveyDashboardViewsRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    surveyId: string;
    projectId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetSurveyDashboardViewsRPCRequest: boolean = true;
  
    readonly surveyId: LeoUUID;
    readonly projectId: LeoUUID;
  
  
    constructor(
    surveyId: LeoUUID, 
    projectId: LeoUUID
    ) {
      this.surveyId = surveyId;
      this.projectId = projectId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      return new Request(
        surveyId,
        projectId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          surveyId: this.surveyId.toDTO(),
          projectId: this.projectId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `GetSurveyDashboardViewsRPC(surveyId='${this.surveyId}',projectId='${this.projectId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    surveyDetailsAndDashboardViews: SurveyDetailsAndDashboardViewsDTO;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetSurveyDashboardViewsResponse: boolean = true;
  
    readonly surveyDetailsAndDashboardViews: SurveyDetailsAndDashboardViews;
  
  
    constructor(
    surveyDetailsAndDashboardViews: SurveyDetailsAndDashboardViews
    ) {
      this.surveyDetailsAndDashboardViews = surveyDetailsAndDashboardViews;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const surveyDetailsAndDashboardViews = SurveyDetailsAndDashboardViews.fromDTO(getObject(dto, "surveyDetailsAndDashboardViews", InvalidResponseSurveyDetailsAndDashboardViewsError));
      return new Response(
        surveyDetailsAndDashboardViews
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          surveyDetailsAndDashboardViews: this.surveyDetailsAndDashboardViews.toDTO()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetSurveyDashboardViews(surveyDetailsAndDashboardViews='${this.surveyDetailsAndDashboardViews}')`;
  };
  

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    InvalidSurveyId = "INVALID_SURVEY_ID"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `GetSurveyDashboardViewsRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `GetSurveyDashboardViewsRPC.INVALID_SURVEY_ID()`;
    };
    

    export type Errors = InvalidProjectId | InvalidSurveyId;
    export type ErrorsDTO = InvalidProjectIdDTO | InvalidSurveyIdDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestSurveyIdError extends GetSurveyDashboardViewsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}

export class InvalidRequestProjectIdError extends GetSurveyDashboardViewsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidResponseSurveyDetailsAndDashboardViewsError extends GetSurveyDashboardViewsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyDetailsAndDashboardViewsError";
  }
}

export class InvalidResponseInvalidProjectIdError extends GetSurveyDashboardViewsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseInvalidSurveyIdError extends GetSurveyDashboardViewsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

